/**
 * PagoOrdenFormDialog - Dialog para registrar pagos parciales/totales de una orden.
 * Soporta tipo de pago (ANTICIPO/ABONO/CANCELACION), calculadora de vuelto en tiempo real
 * y muestra el saldo pendiente de la orden.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CreditCard, Wallet } from "lucide-react";
import { toast } from "sonner";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";

import { usePagarOrden } from "@/hooks/use-ordenes";
import { useMetodosPago } from "@/hooks/use-facturacion";
import { useApiError } from "@/hooks/use-api-error";
import { calcularVueltoOrden } from "@/pages/ordenes/utils/pago-orden-logic";
import type { OrdenListItem, TipoPagoOrden, PagoOrdenResult } from "@/hooks/use-ordenes";

interface PagoOrdenFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orden: OrdenListItem | null;
  onPagoSuccess?: (result: PagoOrdenResult) => void;
}

export function PagoOrdenFormDialog({
  open,
  onOpenChange,
  orden,
  onPagoSuccess,
}: PagoOrdenFormDialogProps) {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiError();
  const [metodoId, setMetodoId] = useState<string>("");
  const [tipoPago, setTipoPago] = useState<TipoPagoOrden>("CANCELACION");
  const [referencia, setReferencia] = useState("");
  const [montoRecibidoRaw, setMontoRecibidoRaw] = useState("");

  const { data: metodosPagoData, isLoading: loadingMetodos } = useMetodosPago();
  const metodosPago = (metodosPagoData?.results ?? []).filter((m) => m.is_active);

  const pagarOrdenMutation = usePagarOrden({
    onSuccess: (result) => {
      toast.success(t("ordenes.pago.successPay"));
      handleClose();
      onPagoSuccess?.(result);
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const handleClose = () => {
    setMetodoId("");
    setTipoPago("CANCELACION");
    setReferencia("");
    setMontoRecibidoRaw("");
    onOpenChange(false);
  };

  const handleSubmit = () => {
    if (!orden || !metodoId || !montoRecibidoRaw) return;
    pagarOrdenMutation.mutate({
      id: orden.id,
      data: {
        metodo: Number(metodoId),
        monto_recibido: parseFloat(montoRecibidoRaw),
        tipo_pago: tipoPago,
        referencia: referencia || undefined,
      },
    });
  };

  // Saldo pendiente: si la orden tiene saldo_pendiente lo usamos, si no usamos total
  const saldoPendiente = orden ? parseFloat(orden.saldo_pendiente ?? orden.total) || 0 : 0;
  const montoRecibido = parseFloat(montoRecibidoRaw) || 0;
  const hasMontoInput = montoRecibidoRaw !== "" && !isNaN(parseFloat(montoRecibidoRaw));
  const montoInsuficiente = hasMontoInput && montoRecibido <= 0;
  const { montoAplicado, vuelto } = calcularVueltoOrden(saldoPendiente, montoRecibido);

  const canSubmit =
    !!metodoId && !!montoRecibidoRaw && !pagarOrdenMutation.isPending && !montoInsuficiente;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            {t("ordenes.pago.registrarPago")}
          </DialogTitle>
          <DialogDescription>
            {orden?.numero} — {orden?.paciente_nombre}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Resumen de saldo */}
          {orden && (
            <div className="rounded-md bg-muted p-3 text-sm space-y-1">
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t("ordenes.pago.totalOrden")}</span>
                <span className="font-mono font-medium">
                  L.{parseFloat(orden.total).toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between font-semibold">
                <span>{t("ordenes.pago.saldoPendiente")}</span>
                <span className="font-mono text-orange-600 dark:text-orange-400">
                  L.{saldoPendiente.toFixed(2)}
                </span>
              </div>
            </div>
          )}

          {/* Tipo de pago */}
          <div className="space-y-1.5">
            <Label>{t("ordenes.pago.tipoPago")} *</Label>
            <Select value={tipoPago} onValueChange={(v) => setTipoPago(v as TipoPagoOrden)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ANTICIPO">{t("ordenes.pago.tipos.ANTICIPO")}</SelectItem>
                <SelectItem value="ABONO">{t("ordenes.pago.tipos.ABONO")}</SelectItem>
                <SelectItem value="CANCELACION">{t("ordenes.pago.tipos.CANCELACION")}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Método de pago */}
          <div className="space-y-1.5">
            <Label>{t("ordenes.pago.method")} *</Label>
            <Select value={metodoId} onValueChange={setMetodoId} disabled={loadingMetodos}>
              <SelectTrigger>
                <SelectValue placeholder={t("ordenes.pago.selectMethod")} />
              </SelectTrigger>
              <SelectContent>
                {metodosPago.map((m) => (
                  <SelectItem key={m.id} value={String(m.id)}>
                    {m.nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Monto recibido */}
          <div className="space-y-1.5">
            <Label htmlFor="monto_recibido">{t("ordenes.pago.montoRecibido")} *</Label>
            <Input
              id="monto_recibido"
              type="number"
              step="0.01"
              min="0.01"
              placeholder="0.00"
              value={montoRecibidoRaw}
              onChange={(e) => setMontoRecibidoRaw(e.target.value)}
              className={montoInsuficiente ? "border-destructive" : ""}
            />
            {montoInsuficiente && (
              <p className="text-sm text-destructive">{t("ordenes.pago.montoInsuficiente")}</p>
            )}
          </div>

          {/* Calculadora de vuelto */}
          {hasMontoInput && !montoInsuficiente && (
            <div className="rounded-md border p-3 space-y-2 text-sm">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <Wallet className="h-4 w-4" />
                <span className="font-medium">{t("ordenes.pago.resumen")}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t("ordenes.pago.saldoPendiente")}</span>
                <span className="font-mono">L.{saldoPendiente.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t("ordenes.pago.montoRecibido")}</span>
                <span className="font-mono">L.{montoRecibido.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t("ordenes.pago.montoAplicado")}</span>
                <span className="font-mono">L.{montoAplicado.toFixed(2)}</span>
              </div>
              <Separator />
              {vuelto > 0 ? (
                <div className="flex justify-between font-semibold text-green-600 dark:text-green-400">
                  <span>{t("ordenes.pago.vuelto")}</span>
                  <span className="font-mono">L.{vuelto.toFixed(2)}</span>
                </div>
              ) : (
                <div className="flex justify-between font-semibold text-green-600 dark:text-green-400">
                  <span>{t("ordenes.pago.pagoExacto")}</span>
                  <span className="font-mono">L.0.00</span>
                </div>
              )}
            </div>
          )}

          {/* Referencia */}
          <div className="space-y-1.5">
            <Label>{t("ordenes.pago.reference")}</Label>
            <Input
              placeholder={t("ordenes.pago.referencePlaceholder")}
              value={referencia}
              onChange={(e) => setReferencia(e.target.value)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={pagarOrdenMutation.isPending}>
            {t("common.cancel")}
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {pagarOrdenMutation.isPending ? t("common.saving") : t("ordenes.pago.confirmPay")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
